// src/host/HostAccess.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../lib/useAuth";

const BRAND = "#7C9082";

/* ---------------------------- Access form shape ---------------------------- */

function emptyAccessForm() {
  return {
    checkInFrom: "",
    checkInTo: "",
    checkOutBy: "",
    accessMethod: "",
    accessSteps: "",
    doorCode: "",
    gateCode: "",
    garageCode: "",
    parkingInstructions: "",
    wifiName: "",
    wifiPassword: "",
    houseManual: "",
  };
}

function fromListingAccess(access = {}) {
  return {
    checkInFrom: access.checkInFrom || "",
    checkInTo: access.checkInTo || "",
    checkOutBy: access.checkOutBy || "",
    accessMethod: access.accessMethod || "",
    accessSteps: access.accessSteps || "",
    doorCode: access.doorCode || "",
    gateCode: access.gateCode || "",
    garageCode: access.garageCode || "",
    parkingInstructions: access.parkingInstructions || "",
    wifiName: access.wifiName || "",
    wifiPassword: access.wifiPassword || "",
    houseManual: access.houseManual || "",
  };
}

function toAccessPayload(form) {
  const out = {};
  for (const [k, v] of Object.entries(form)) {
    out[k] = typeof v === "string" ? v.trim() : v;
  }
  return out;
}

function accessMethodLabel(value) {
  switch (value) {
    case "smart_lock":
      return "Smart lock entry";
    case "keypad":
      return "Keypad entry";
    case "lockbox":
      return "Lockbox with key";
    case "doorman":
      return "Doorman / concierge";
    case "meet_host":
      return "Meet your host in person";
    case "gate_code":
      return "Gated entry with code";
    case "other":
      return "Custom access method";
    default:
      return "Access details";
  }
}

/* ------------------------------ UI bits ------------------------------ */

function StepPill({ index, label, step }) {
  const isActive = step === index;
  const isDone = step > index;
  return (
    <div className="flex items-center gap-2">
      <span
        className={[
          "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
          isActive
            ? "bg-black text-white"
            : isDone
            ? "bg-black/5 text-neutral-800"
            : "bg-neutral-100 text-neutral-500",
        ].join(" ")}
      >
        {index}
      </span>
      <span
        className={[
          "text-xs font-medium",
          isActive ? "text-neutral-900" : "text-neutral-500",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-medium text-neutral-800">
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={[
        "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm",
        "placeholder:text-neutral-400",
        "focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900",
        props.className || "",
      ].join(" ")}
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={[
        "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm",
        "placeholder:text-neutral-400",
        "focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900",
        "resize-y",
        props.className || "",
      ].join(" ")}
    />
  );
}

/* ----------------------------- Component ----------------------------- */

export default function HostAccess() {
  const { user, loading: authLoading } = useAuth();

  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [selectedListingId, setSelectedListingId] = useState("");

  const [form, setForm] = useState(emptyAccessForm());
  const [step, setStep] = useState(1);

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const [showMoreCodes, setShowMoreCodes] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  /* ------------------------------ Data loading ------------------------------ */

  // Load host listings
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!user || authLoading) {
        setListings([]);
        setListingsLoading(false);
        return;
      }

      try {
        setListingsLoading(true);

        // Primary: ownerUid
        const qListings = query(
          collection(db, "listings"),
          where("ownerUid", "==", user.uid)
        );

        const snap = await getDocs(qListings);
        if (cancelled) return;

        const docs = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));

        setListings(docs);

        if (!selectedListingId && docs.length > 0) {
          const first = docs[0];
          setSelectedListingId(first.id);
          setForm(fromListingAccess(first.access || {}));
        }
      } catch (e) {
        console.error("[HostAccess] load listings error:", e);
        if (!cancelled) {
          setError("We couldn’t load your listings. Try again in a moment.");
        }
      } finally {
        if (!cancelled) setListingsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, selectedListingId]);

  // When you change listings, hydrate the form
  useEffect(() => {
    if (!selectedListingId || !listings.length) return;
    const listing = listings.find((l) => l.id === selectedListingId);
    if (!listing) return;
    setForm(fromListingAccess(listing.access || {}));
    setSavedAt(null);
    setError("");
  }, [selectedListingId, listings]);

  const selectedListing = useMemo(
    () => listings.find((l) => l.id === selectedListingId) || null,
    [listings, selectedListingId]
  );

  /* ------------------------------ Handlers ------------------------------ */

  function handleFieldChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSavedAt(null);
    setError("");
  }

  async function saveAndMaybeGo(stepAfter) {
    if (!user) {
      setError("You need to be signed in as a host to save these details.");
      return;
    }
    if (!selectedListing) {
      setError("Select a listing first.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const ref = doc(db, "listings", selectedListing.id);
      const payload = toAccessPayload(form);

      await updateDoc(ref, {
        access: payload,
        accessUpdatedAt: serverTimestamp(),
      });

      setListings((prev) =>
        prev.map((l) =>
          l.id === selectedListing.id ? { ...l, access: payload } : l
        )
      );

      setSavedAt(new Date());
      if (typeof stepAfter === "number") {
        setStep(stepAfter);
      }
    } catch (e) {
      console.error("[HostAccess] save error:", e);
      setError(
        e?.message ||
          "We couldn’t save your changes. Please check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  }

  const canUseForm =
    !authLoading && !listingsLoading && !!selectedListing && !!user;
  const disableButtons = saving || !canUseForm;

  /* ------------------------------- Early UI ------------------------------- */

  if (authLoading) {
    return (
      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-semibold">Check-in & access</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Loading your listings…
        </p>
      </div>
    );
  }

  if (!user) return null;

  /* --------------------------------- View --------------------------------- */

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      {/* Header */}
      <header className="pt-1">
        <div className="inline-flex items-center gap-2 rounded-full bg-neutral-100 px-3 py-1 text-[11px] font-medium text-neutral-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Guest-facing details
        </div>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-neutral-900">
          Check-in &amp; access
        </h1>
        <p className="mt-2 max-w-xl text-sm text-neutral-600">
          Set simple, clear instructions that confirmed guests see on their trip
          page and reminder emails. The full street address, codes, and Wi-Fi
          details only show after a booking is fully paid; before that, guests
          only see the general area (city &amp; state).
        </p>
        {savedAt && (
          <p className="mt-1 text-xs text-neutral-500">
            Last saved{" "}
            {savedAt.toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            })}
          </p>
        )}
      </header>

      {/* Listing selector */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-medium text-neutral-900">
              Choose a listing
            </h2>
            <p className="mt-1 text-xs text-neutral-500">
              Each listing can have its own check-in, access, and Wi-Fi details.
            </p>
          </div>

          {listingsLoading ? (
            <div className="h-9 w-48 animate-pulse rounded-md bg-neutral-100" />
          ) : listings.length === 0 ? (
            <div className="flex flex-col items-start gap-2 text-sm text-neutral-600">
              <p>You don’t have any listings yet.</p>
              <Link
                to="/host/listings/new"
                className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
              >
                Add your first listing
              </Link>
            </div>
          ) : (
            <select
              value={selectedListingId}
              onChange={(e) => setSelectedListingId(e.target.value)}
              className="w-full max-w-xs rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900"
            >
              {listings.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title || l.name || `Listing ${l.id.slice(0, 6)}…`}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedListing && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl bg-neutral-50 px-3 py-2.5">
            <div className="h-10 w-12 shrink-0 rounded-xl bg-neutral-200/80" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-neutral-900">
                {selectedListing.title ||
                  selectedListing.name ||
                  "Your listing"}
              </p>
              {(selectedListing.city || selectedListing.stateCode) && (
                <p className="truncate text-xs text-neutral-500">
                  {[selectedListing.city, selectedListing.stateCode]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
              <p className="mt-0.5 text-[11px] text-neutral-500">
                Guests see these details on their trip page once their booking
                is confirmed and paid.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="ml-auto rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-100"
            >
              Preview as guest
            </button>
          </div>
        )}
      </section>

      {/* If there is no listing, stop here */}
      {!selectedListing && !listingsLoading && (
        <p className="text-sm text-neutral-500">
          Create a listing first to add check-in instructions.
        </p>
      )}

      {selectedListing && (
        <>
          {/* Step header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <StepPill index={1} label="Times" step={step} />
              <StepPill index={2} label="Getting in" step={step} />
              <StepPill index={3} label="Wi-Fi & house manual" step={step} />
            </div>
          </div>

          {/* Main card (wizard) */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5">
            {/* Step 1: Times */}
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-900">
                    Check-in &amp; check-out
                  </h2>
                  <p className="mt-1 text-xs text-neutral-500">
                    Set the window guests can arrive and when they should
                    leave.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>Check-in from</Label>
                    <Input
                      name="checkInFrom"
                      value={form.checkInFrom}
                      onChange={handleFieldChange}
                      placeholder="3:00 PM"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Check-in until</Label>
                    <Input
                      name="checkInTo"
                      value={form.checkInTo}
                      onChange={handleFieldChange}
                      placeholder="10:00 PM"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Check-out by</Label>
                    <Input
                      name="checkOutBy"
                      value={form.checkOutBy}
                      onChange={handleFieldChange}
                      placeholder="11:00 AM"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Getting in */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-900">
                    Getting into the property
                  </h2>
                  <p className="mt-1 text-xs text-neutral-500">
                    Explain how guests arrive, where to park, and how they
                    unlock the door.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
                  <div className="space-y-1.5">
                    <Label>Access method</Label>
                    <select
                      name="accessMethod"
                      value={form.accessMethod}
                      onChange={handleFieldChange}
                      className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900"
                    >
                      <option value="">Select a method</option>
                      <option value="smart_lock">Smart lock</option>
                      <option value="keypad">Keypad</option>
                      <option value="lockbox">Lockbox</option>
                      <option value="doorman">Doorman / concierge</option>
                      <option value="meet_host">Meet host in person</option>
                      <option value="gate_code">Gated entry with code</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Step-by-step instructions</Label>
                    <Textarea
                      name="accessSteps"
                      value={form.accessSteps}
                      onChange={handleFieldChange}
                      rows={5}
                      placeholder="Example: Park in the driveway on the right. Walk to the front door, the keypad is on your left. Enter code 1234# and turn the handle."
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5 md:max-w-xs">
                    <Label>Door / smart lock code</Label>
                    <Input
                      name="doorCode"
                      value={form.doorCode}
                      onChange={handleFieldChange}
                      placeholder="1234#"
                    />
                    <p className="text-[11px] text-neutral-500">
                      Only visible to confirmed, paid guests with an active
                      booking. For safety, change codes regularly.
                    </p>
                  </div>

                  {!showMoreCodes && (
                    <button
                      type="button"
                      onClick={() => setShowMoreCodes(true)}
                      className="text-xs font-medium text-neutral-700 underline underline-offset-2"
                    >
                      Add gate or garage code
                    </button>
                  )}

                  {showMoreCodes && (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>Gate code (optional)</Label>
                        <Input
                          name="gateCode"
                          value={form.gateCode}
                          onChange={handleFieldChange}
                          placeholder="*5678"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Garage / parking code (optional)</Label>
                        <Input
                          name="garageCode"
                          value={form.garageCode}
                          onChange={handleFieldChange}
                          placeholder="A901"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Parking instructions</Label>
                  <Textarea
                    name="parkingInstructions"
                    value={form.parkingInstructions}
                    onChange={handleFieldChange}
                    rows={3}
                    placeholder="Example: You may park in the driveway or along the street directly in front of the house. Please do not block neighbor driveways or park in the alley."
                  />
                </div>
              </div>
            )}

            {/* Step 3: Wi-Fi & house manual */}
            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-900">
                    Wi-Fi &amp; house manual
                  </h2>
                  <p className="mt-1 text-xs text-neutral-500">
                    Share network details and anything guests should treat
                    gently.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Wi-Fi network name</Label>
                    <Input
                      name="wifiName"
                      value={form.wifiName}
                      onChange={handleFieldChange}
                      placeholder="YoureWelcome-Guest"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Wi-Fi password</Label>
                    <Input
                      name="wifiPassword"
                      value={form.wifiPassword}
                      onChange={handleFieldChange}
                      placeholder="welcome-home-123"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>House manual</Label>
                  <Textarea
                    name="houseManual"
                    value={form.houseManual}
                    onChange={handleFieldChange}
                    rows={5}
                    placeholder="Example: Please remove shoes at the door. Trash bins are behind the house; blue for recycling, green for trash. Quiet hours after 10 PM. The thermostat can be adjusted between 68–74°F."
                  />
                </div>
              </div>
            )}

            {/* Footer actions */}
            <div className="mt-6 flex flex-col gap-3 border-t border-neutral-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {step > 1 && (
                  <button
                    type="button"
                    disabled={disableButtons}
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  disabled={disableButtons}
                  onClick={() =>
                    saveAndMaybeGo(step < 3 ? step + 1 : undefined)
                  }
                  className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-medium text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ backgroundColor: BRAND }}
                >
                  {saving
                    ? "Saving…"
                    : step < 3
                    ? "Save & continue"
                    : "Save"}
                </button>
              </div>

              <div className="text-xs text-neutral-500 sm:text-right">
                {error ? (
                  <span className="text-red-500">{error}</span>
                ) : savedAt ? (
                  <>All changes saved.</>
                ) : (
                  <>
                    Guests will only see this after their booking is confirmed
                    and paid.
                  </>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Guest preview modal */}
      {previewOpen && selectedListing && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPreviewOpen(false);
          }}
        >
          <div
            className="mx-auto w-full max-w-md rounded-3xl bg-white p-4 shadow-xl sm:p-5"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Guest preview
                </p>
                <p className="text-sm font-medium text-neutral-900">
                  What a confirmed guest will see
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800">
              {/* Address (gated explanation) */}
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Address
                </p>
                <p className="mt-0.5 text-xs text-neutral-700">
                  {[
                    selectedListing.addressLine1 || selectedListing.address,
                    [selectedListing.city, selectedListing.stateCode || selectedListing.state]
                      .filter(Boolean)
                      .join(", "),
                  ]
                    .filter(Boolean)
                    .join(", ") || "Your listing’s address will appear here."}
                </p>
                <p className="mt-1 text-[10px] text-neutral-500">
                  Guests only see the full street address after their booking is
                  confirmed and paid. Before payment, they only see the general
                  area (city and state).
                </p>
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Check-in
                </p>
                <p className="mt-0.5">
                  {form.checkInFrom || form.checkInTo
                    ? [
                        form.checkInFrom && `From ${form.checkInFrom}`,
                        form.checkInTo && `until ${form.checkInTo}`,
                      ]
                        .filter(Boolean)
                        .join(" ")
                    : "Host hasn’t set check-in times yet."}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Check-out
                </p>
                <p className="mt-0.5">
                  {form.checkOutBy || "Host hasn’t set check-out time yet."}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Access
                </p>
                <p className="mt-0.5">
                  {form.accessMethod
                    ? accessMethodLabel(form.accessMethod)
                    : "Access details will be shared before arrival."}
                </p>
                {form.accessSteps && (
                  <p className="mt-1 text-xs text-neutral-700">
                    {form.accessSteps}
                  </p>
                )}
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Codes
                </p>
                <ul className="mt-0.5 space-y-0.5 text-xs text-neutral-700">
                  {form.doorCode && (
                    <li>
                      <span className="font-medium">Door:</span>{" "}
                      {form.doorCode}
                    </li>
                  )}
                  {form.gateCode && (
                    <li>
                      <span className="font-medium">Gate:</span>{" "}
                      {form.gateCode}
                    </li>
                  )}
                  {form.garageCode && (
                    <li>
                      <span className="font-medium">Garage:</span>{" "}
                      {form.garageCode}
                    </li>
                  )}
                  {!form.doorCode &&
                    !form.gateCode &&
                    !form.garageCode && (
                      <li className="text-neutral-500">
                        No codes added yet.
                      </li>
                    )}
                </ul>
                <p className="mt-1 text-[10px] text-neutral-500">
                  For security, codes are only shown for confirmed, paid
                  bookings and should be changed between guests.
                </p>
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Parking
                </p>
                <p className="mt-0.5 text-xs text-neutral-700">
                  {form.parkingInstructions ||
                    "Host hasn’t added parking instructions yet."}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Wi-Fi
                </p>
                {form.wifiName || form.wifiPassword ? (
                  <p className="mt-0.5 text-xs text-neutral-700">
                    <span className="font-medium">
                      {form.wifiName || "Network"}
                    </span>
                    {form.wifiPassword && ` • ${form.wifiPassword}`}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-neutral-500">
                    Wi-Fi details will be shared closer to check-in.
                  </p>
                )}
              </div>

              {form.houseManual && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                    House manual
                  </p>
                  <p className="mt-0.5 whitespace-pre-line text-xs text-neutral-700">
                    {form.houseManual}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
